
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { BoListPanel } from 'bonomo/dist';
import { IOnOffHandlers } from 'bonomo';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import {
  IServiceProviderMonthlyMetric,
  EServiceProviderOrderType,
  ECommissionType,
} from '@/model/ServiceProvider/serviceProviderMetricsInterfaces';
import { EPermissionActions } from '@/model/EPermissionActions';
import MonthCloseConfirmDialog from '@/components/serviceProvider/metrics/MonthCloseConfirmDialog.vue';
import CsvDownloader from '@/components/shared/CsvDownloader.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';

const serviceProviderMetricsModule = namespace('serviceProviderMetricsModule');
const authModule = namespace('authModule');

const AUTO_GMV_TYPES = [EServiceProviderOrderType.AUTOMATIC];
const today = new Date();
// The active (editable) period is always the previous month
const activePeriodMonth = today.getMonth() === 0 ? 12 : today.getMonth();
const activePeriodYear  = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

@Component({
  components: { BoListPanel, MonthCloseConfirmDialog, CsvDownloader, PaginationBar },
})
export default class ServiceProviderMetrics extends Vue {
  public EServiceProviderOrderType = EServiceProviderOrderType;
  public ECommissionType = ECommissionType;
  public subscribes: IOnOffHandlers = {};

  public filtersExpanded: boolean = false;
  public filterStatus: string[] = [];
  public filterOrderType: string[] = [];

  public currentPage: number = 1;
  public perPage: number = 15;
  public readonly perPageOptions: number[] = [15, 30, 50];

  public showCloseDialog = false;
  public editingRows: Record<number, boolean> = {};
  public localYear: number = activePeriodYear;
  public localMonth: number = activePeriodMonth;

  // ---- vuex ---------------------------------------------------------------

  @authModule.Getter('canAction') canAction!: (action: string) => boolean;

  @serviceProviderMetricsModule.Getter('metricsList')
  protected metricsList!: IServiceProviderMonthlyMetric[];

  @serviceProviderMetricsModule.Getter('isPeriodClosed')
  protected isPeriodClosed!: boolean;

  @serviceProviderMetricsModule.Getter('isAllComplete')
  protected isAllComplete!: boolean;

  @serviceProviderMetricsModule.Getter('incompleteCount')
  protected incompleteCount!: number;

  @serviceProviderMetricsModule.Getter('pendingCount')
  protected pendingCount!: number;

  @serviceProviderMetricsModule.Getter('awaitingValidationCount')
  protected awaitingValidationCount!: number;

  @serviceProviderMetricsModule.Action('initMetrics')
  protected initMetrics!: (payload: { year: number; month: number }) => void;

  @serviceProviderMetricsModule.Action('setSelectedPeriod')
  protected setSelectedPeriod!: (payload: { year: number; month: number }) => void;

  @serviceProviderMetricsModule.Action('updateMetricField')
  protected updateMetricField!: (payload: {
    serviceProviderId: number;
    field: keyof IServiceProviderMonthlyMetric;
    value: any;
  }) => void;

  @serviceProviderMetricsModule.Action('saveRow')
  protected saveRowAction!: (serviceProviderId: number) => void;

  @serviceProviderMetricsModule.Action('closeMonth')
  protected closeMonth!: () => void;

  // ---- lifecycle ----------------------------------------------------------

  created(): void {
    this.subscribes = this.$ons({
      'backend.call.OK.metricsLoaded': this.onMetricsLoaded,
      'backend.call.OK.metricSaved':   this.onMetricSavedOK,
      'backend.call.KO.metricSaved':   this.onMetricSavedKO,
      'backend.call.OK.monthClosed':   this.onMonthClosedOK,
      'backend.call.KO.monthClosed':   this.onMonthClosedKO,
    });
  }

  beforeDestroy(): void {
    this.$offs(this.subscribes);
  }

  protected loadData(): void {
    this.initMetrics({ year: this.localYear, month: this.localMonth });
  }

  protected onMetricsLoaded(): void {
    this.filterStatus = this.isPeriodClosed ? ['complete'] : [];
    this.filterOrderType = [];
  }

  protected onMetricSavedOK(): void {
    this.$uiBus.emit('success', this.$t('common.saved'));
  }

  protected onMetricSavedKO(): void {
    this.$uiBus.emit('alert', this.$t('common.error'));
  }

  protected onMonthClosedOK(): void {
    this.$uiBus.emit('success', this.$t('serviceProvider.metrics.monthClosedOK'));
  }

  protected onMonthClosedKO(): void {
    this.$uiBus.emit('alert', this.$t('common.error'));
  }

  // ---- computed: permissions ----------------------------------------------

  protected get canEdit(): boolean {
    return this.canAction(EPermissionActions.ACTION_SERVICE_PROVIDERS_METRICS);
  }

  protected get restrictedView(): boolean {
    return !this.canEdit && !this.isPeriodClosed;
  }

  protected get tableItems(): IServiceProviderMonthlyMetric[] {
    if (this.restrictedView) return [];
    return this.paginatedMetrics;
  }

  // ---- computed: period ---------------------------------------------------

  // True when viewing the current calendar month (read-only preview)
  protected get isPreviewPeriod(): boolean {
    return (
      this.localYear === today.getFullYear() &&
      this.localMonth === today.getMonth() + 1
    );
  }

  // True when viewing the previous month (the editable/closeable period)
  protected get isActivePeriod(): boolean {
    return this.localYear === activePeriodYear && this.localMonth === activePeriodMonth;
  }

  protected get periodStatusChip(): { text: string; color: string; textColor: string } {
    if (this.isPreviewPeriod) {
      return { text: this.$t('serviceProvider.metrics.periodPreview') as string, color: 'blue lighten-4', textColor: 'blue darken-3' };
    }
    if (this.isPeriodClosed) {
      return { text: this.$t('serviceProvider.metrics.periodClosed') as string, color: 'blue-grey lighten-4', textColor: 'blue-grey darken-3' };
    }
    if (this.isActivePeriod) {
      return { text: this.$t('serviceProvider.metrics.periodOpen') as string, color: 'green lighten-4', textColor: 'green darken-3' };
    }
    return { text: this.$t('serviceProvider.metrics.periodNotClosed') as string, color: 'orange lighten-4', textColor: 'orange darken-3' };
  }

  protected isEditable(item: IServiceProviderMonthlyMetric): boolean {
    if (!this.canEdit) return false;
    if (!this.isActivePeriod) return false;
    const status = this.rowStatus(item);
    if (status === 'incomplete' || status === 'awaitingValidation') return true;
    return this.editingRows[item.serviceProviderId];
  }

  protected isGmvReadOnly(item: IServiceProviderMonthlyMetric): boolean {
    return AUTO_GMV_TYPES.indexOf(item.orderType) !== -1;
  }

  protected get yearItems(): number[] {
    const current = today.getFullYear();
    return [current - 2, current - 1, current];
  }

  protected get monthItems(): { text: string; value: number; disabled?: boolean }[] {
    const locale = this.$i18n.locale;
    return Array.from({ length: 12 }, (_, i) => ({
      text: new Date(2000, i, 1).toLocaleString(locale, { month: 'long' }).replace(/^\w/, (c) => c.toUpperCase()),
      value: i + 1,
      disabled: this.localYear === today.getFullYear() && i + 1 > today.getMonth() + 1, // preview month is the max
    }));
  }

  protected get periodLabel(): string {
    const month = this.monthItems.find((m) => m.value === this.localMonth);
    return `${month ? month.text : ''} ${this.localYear}`;
  }

  // ---- computed: filters --------------------------------------------------

  protected get hasActiveFilters(): boolean {
    return this.filterStatus.length > 0 || this.filterOrderType.length > 0;
  }

  protected get activeFilterCount(): number {
    return this.filterStatus.length + this.filterOrderType.length;
  }

  protected get statusFilterOptions() {
    return [
      { text: this.$t('serviceProvider.metrics.statusComplete'),            value: 'complete',            color: 'green lighten-4',  textColor: 'green darken-3' },
      { text: this.$t('serviceProvider.metrics.statusAwaitingValidation'),  value: 'awaitingValidation',  color: 'blue lighten-4',   textColor: 'blue darken-3' },
      { text: this.$t('serviceProvider.metrics.statusPending'),             value: 'pending',             color: 'orange lighten-4', textColor: 'orange darken-3' },
      { text: this.$t('serviceProvider.metrics.statusIncomplete'),          value: 'incomplete',          color: 'red lighten-4',    textColor: 'red darken-3' },
    ];
  }

  // ---- computed: data (filtered + paginated) ------------------------------

  protected get filteredMetrics(): IServiceProviderMonthlyMetric[] {
    let list = this.metricsList;
    if (this.filterStatus.length > 0) {
      list = list.filter((m) =>
        this.filterStatus.indexOf(this.rowStatus(m)) !== -1 ||
        this.editingRows[m.serviceProviderId]
      );
    }
    if (this.filterOrderType.length > 0) {
      list = list.filter((m) => this.filterOrderType.indexOf(m.orderType) !== -1);
    }
    return list;
  }

  protected get lastPage(): number {
    return Math.max(1, Math.ceil(this.filteredMetrics.length / this.perPage));
  }

  protected get paginatedMetrics(): IServiceProviderMonthlyMetric[] {
    const start = (this.currentPage - 1) * this.perPage;
    return this.filteredMetrics.slice(start, start + this.perPage);
  }

  // ---- computed: select items ---------------------------------------------

  protected get orderTypeItems(): { value: EServiceProviderOrderType; label: string }[] {
    return Object.values(EServiceProviderOrderType).map((v) => ({
      value: v,
      label: this.$t('serviceProvider.metrics.orderTypes.' + v) as string,
    }));
  }

  protected get orderTypeEditItems(): { value: EServiceProviderOrderType; label: string }[] {
    return this.orderTypeItems.filter((v) => v.value !== EServiceProviderOrderType.AUTOMATIC);
  }

  protected get commissionTypeItems(): { value: ECommissionType; label: string }[] {
    return Object.values(ECommissionType)
      .filter((v) => v !== ECommissionType.REAL)
      .map((v) => ({
        value: v,
        label: this.$t('serviceProvider.metrics.commissionTypes.' + v) as string,
      }));
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: '', value: 'status', sortable: false, align: 'center', width: '36px' },
      { text: 'ID', value: 'serviceProviderId', sortable: false, align: 'center', width: '70px' },
      { text: this.$t('common.name') as string, value: 'serviceProviderName', sortable: false, width: '180px' },
      { text: this.$t('serviceProvider.metrics.scopeSize') as string, value: 'scopeSize', sortable: false, align: 'center', width: '90px' },
      { text: this.$t('serviceProvider.metrics.visit') as string, value: 'visit', sortable: false, align: 'center', width: '90px' },
      { text: this.$t('serviceProvider.metrics.purchaseIntent') as string, value: 'purchaseIntent', sortable: false, align: 'center', width: '90px' },
      { text: this.$t('serviceProvider.metrics.orders') as string, value: 'orders', sortable: false, width: '220px' },
      { text: this.$t('serviceProvider.metrics.gmv') as string, value: 'gmv', sortable: false, width: '130px' },
      { text: this.$t('serviceProvider.metrics.commission') as string, value: 'commission', sortable: false, width: '280px' },
      { text: '', value: 'actions', sortable: false, align: 'center', width: '48px' },
    ];
  }

  // ---- event handlers -----------------------------------------------------

  protected onFilterChange(): void {
    this.currentPage = 1;
  }

  protected toggleStatusFilter(value: string): void {
    const idx = this.filterStatus.indexOf(value);
    if (idx === -1) {
      this.filterStatus = [...this.filterStatus, value];
    } else {
      this.filterStatus = this.filterStatus.filter((s) => s !== value);
    }
    this.currentPage = 1;
  }

  protected onPeriodChange(): void {
    this.filterOrderType = [];
    this.currentPage = 1;
    this.setSelectedPeriod({ year: this.localYear, month: this.localMonth });
    this.filterStatus = this.isPeriodClosed ? ['complete'] : ['incomplete', 'pending', 'awaitingValidation'];
  }

  protected goToActivePeriod(): void {
    this.localYear = activePeriodYear;
    this.localMonth = activePeriodMonth;
    this.onPeriodChange();
  }

  protected prevMonth(): void {
    if (this.localMonth === 1) { this.localMonth = 12; this.localYear--; }
    else { this.localMonth--; }
    this.onPeriodChange();
  }

  protected nextMonth(): void {
    if (this.localMonth === 12) { this.localMonth = 1; this.localYear++; }
    else { this.localMonth++; }
    this.onPeriodChange();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
  }

  protected onPerPageChange(value: number): void {
    this.perPage = value;
    this.currentPage = 1;
  }

  protected onFieldInput(
    item: IServiceProviderMonthlyMetric,
    field: keyof IServiceProviderMonthlyMetric,
    value: any
  ): void {
    // Keep row in editing mode so it doesn't "close" while user is typing
    this.$set(this.editingRows, item.serviceProviderId, true);
    this.updateMetricField({ serviceProviderId: item.serviceProviderId, field, value });
  }

  protected onEditRow(item: IServiceProviderMonthlyMetric): void {
    this.$set(this.editingRows, item.serviceProviderId, true);
  }

  protected onSaveRow(item: IServiceProviderMonthlyMetric): void {
    if (this.rowStatus(item) === 'incomplete') {
      this.$uiBus.emit('alert', this.$t('serviceProvider.metrics.saveValidationError'));
      return;
    }
    this.saveRowAction(item.serviceProviderId);
    this.$set(this.editingRows, item.serviceProviderId, false);
  }

  protected onCloseMonth(): void {
    this.closeMonth();
  }

  // ---- CSV ----------------------------------------------------------------

  protected get csvHeaders(): string[] {
    return [
      'name', 'order_type', 'order_count',
      'active_cards', 'card_clicks', 'cta_clicks',
      'gmv', 'commission_rate', 'commission_type', 'commission_value',
    ];
  }

  protected get csvFileName(): string {
    return `sp_metrics_${this.localYear}_${String(this.localMonth).padStart(2, '0')}.csv`;
  }

  protected fetchCsvData(): Promise<IServiceProviderMonthlyMetric[]> {
    return Promise.resolve(this.metricsList);
  }

  protected csvMapRow(item: IServiceProviderMonthlyMetric): Array<string | number | null> {
    const commissionVal = item.commissionType === ECommissionType.MANUAL
      ? item.commissionManualValue
      : this.commissionCalculated(item);
    return [
      item.serviceProviderName, item.orderType, item.orderCount,
      item.scopeSize, item.visit, item.purchaseIntent,
      item.gmv, item.commissionRate !== null ? Math.round(item.commissionRate * 100) : null, item.commissionType, commissionVal,
    ];
  }

  // ---- helpers ------------------------------------------------------------

  protected rowStatus(item: IServiceProviderMonthlyMetric): 'complete' | 'pending' | 'incomplete' | 'awaitingValidation' {
    if (item.isClosed) return 'complete';
    if (item.orderType === EServiceProviderOrderType.AUTOMATIC && item.orderCount === 0) return 'pending';
    let missing = 0;
    if (item.scopeSize === null) missing++;
    if (item.visit === null) missing++;
    if (item.purchaseIntent === null) missing++;
    if (item.orderCount === null) missing++;
    if (!this.isGmvReadOnly(item) && item.gmv === null) missing++;
    if (item.commissionRate === null) missing++;
    if (item.commissionType === null) missing++;
    if (item.commissionType === ECommissionType.MANUAL && item.commissionManualValue === null) missing++;
    if (missing > 0) return 'incomplete';
    if (!item.isClosed) return 'awaitingValidation';
    return 'complete';
  }

  protected trafficLightColor(item: IServiceProviderMonthlyMetric): string {
    const s = this.rowStatus(item);
    if (s === 'complete') return 'green';
    if (s === 'awaitingValidation') return 'blue';
    if (s === 'pending') return 'orange';
    return 'red';
  }

  protected commissionCalculated(item: IServiceProviderMonthlyMetric): number | null {
    if (item.gmv === null || item.commissionRate === null) return null;
    return Math.round(item.gmv * item.commissionRate * 100) / 100;
  }

  protected formatPercent(rate: number | null): string {
    if (rate === null) return '—';
    return `${(rate * 100).toFixed(1)}%`;
  }

  protected currencySymbol(item: IServiceProviderMonthlyMetric): string {
    const symbols: Record<string, string> = { EUR: '€', GBP: '£' };
    return symbols[item.currency] || '€';
  }

  protected orderTypeColor(type: EServiceProviderOrderType): string {
    const colors: Record<EServiceProviderOrderType, string> = {
      [EServiceProviderOrderType.AUTOMATIC]: 'deep-purple',
      [EServiceProviderOrderType.B2B_ACCOUNT]: 'blue',
      [EServiceProviderOrderType.PARTNER_FEEDBACK]: 'green',
      [EServiceProviderOrderType.ESTIMATE]: 'orange',
    };
    return colors[type] || 'grey';
  }

  protected commissionTypeColor(type: ECommissionType): string {
    const colors: Record<ECommissionType, string> = {
      [ECommissionType.REAL]: 'green darken-1',
      [ECommissionType.ESTIMATED]: 'orange darken-1',
      [ECommissionType.MANUAL]: 'blue-grey',
    };
    return colors[type] || 'grey';
  }
}
